import { useEffect, useRef, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { logoFor } from "@/lib/brandLogos";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANES = ["bottom", "left", "right"]; // 🚂 bottom rail + both side rails — one train per rail
const CAR_SIZE = 44;
const GAP = 6;
const ENGINE_LEN = 88;
const CABOOSE_LEN = 64;
const BUSY_THRESHOLD = 150; // 150+ businesses nearby = longer trains + KINGDOM LINE

// Flagship rides gold; THE KINGDOM LINE rolls out royal purple.
const THEMES = {
  gold: {
    engine: "/pog-express-engine.png",
    carBorder: "border-sky-500/40",
    labelCls: "text-amber-300/90",
    beam: "rgba(255,214,110,0.55)",
  },
  royal: {
    engine: "/kingdom-line-engine.png",
    carBorder: "border-purple-400/60",
    labelCls: "text-purple-300",
    beam: "rgba(150,220,255,0.55)",
  },
};

const isNight = () => { const h = new Date().getHours(); return h >= 19 || h < 6; };

// 🔦 Glowing headlight beam in front of the engine — night runs only.
const Headlight = ({ vertical, dir, color }) => {
  const gold = color || "rgba(255,214,110,0.55)";
  if (!vertical) {
    const right = dir === 1;
    return <span data-testid="pog-express-headlight" className="absolute pointer-events-none" style={{
      top: "50%", transform: "translateY(-50%)", [right ? "left" : "right"]: "88%",
      width: 64, height: 34, filter: "blur(1px)",
      background: `linear-gradient(${right ? "90deg" : "270deg"}, ${gold}, rgba(255,214,110,0))`,
      clipPath: right ? "polygon(0 38%, 100% 0, 100% 100%, 0 62%)" : "polygon(100% 38%, 0 0, 0 100%, 100% 62%)",
    }} />;
  }
  const up = dir === 1;
  return <span data-testid="pog-express-headlight" className="absolute pointer-events-none" style={{
    left: "50%", transform: "translateX(-50%)", [up ? "bottom" : "top"]: "88%",
    width: 34, height: 64, filter: "blur(1px)",
    background: `linear-gradient(${up ? "0deg" : "180deg"}, ${gold}, rgba(255,214,110,0))`,
    clipPath: up ? "polygon(38% 100%, 0 0, 100% 0, 62% 100%)" : "polygon(38% 0, 0 100%, 100% 100%, 62% 0)",
  }} />;
};


// Soft synthesized "toot-toot" — no audio asset needed. Autoplay-safe: stays
// silent until the browser allows audio (after first user interaction).
let _whistleCtx = null;
const ensureWhistleCtx = () => {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  _whistleCtx = _whistleCtx || new AC();
  if (_whistleCtx.state === "suspended") _whistleCtx.resume().catch(() => {});
  return _whistleCtx;
};
// Unlock audio on the very first tap/keypress so the horn works from the first
// train — new phones keep audio locked until a user gesture.
if (typeof window !== "undefined") {
  const unlock = () => {
    ensureWhistleCtx();
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("touchstart", unlock);
    window.removeEventListener("keydown", unlock);
  };
  window.addEventListener("pointerdown", unlock);
  window.addEventListener("touchstart", unlock);
  window.addEventListener("keydown", unlock);
}
const playWhistle = () => {
  try {
    if (!ensureWhistleCtx()) return;
    if (_whistleCtx.state !== "running") return;
    const t0 = _whistleCtx.currentTime;
    const master = _whistleCtx.createGain();
    master.connect(_whistleCtx.destination);
    [554, 659, 831].forEach((f) => {
      const o = _whistleCtx.createOscillator();
      o.type = "triangle";
      o.frequency.value = f;
      o.connect(master);
      o.start(t0);
      o.stop(t0 + 1.1);
    });
    const g = master.gain;
    g.setValueAtTime(0.0001, t0);
    g.exponentialRampToValueAtTime(0.045, t0 + 0.05);
    g.exponentialRampToValueAtTime(0.006, t0 + 0.32);
    g.exponentialRampToValueAtTime(0.045, t0 + 0.42);
    g.exponentialRampToValueAtTime(0.0001, t0 + 1.05);
  } catch { /* audio unavailable — stay quiet */ }
};

// Puffing steam trailing behind the engine, drifting away from the track.
const SmokePuffs = ({ vertical, dir, lane }) => {
  const dx = vertical ? (lane === "right" ? -26 : 26) : -dir * 38;
  const dy = vertical ? dir * 38 : -30;
  return (
    <div className="absolute inset-0 pointer-events-none overflow-visible" data-testid="pog-express-smoke">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="absolute w-3.5 h-3.5 rounded-full bg-slate-100/70 blur-[1px]"
          style={{ left: "calc(50% - 7px)", top: vertical ? "calc(50% - 7px)" : "6%" }}
          animate={{ opacity: [0, 0.7, 0], scale: [0.4, 1.4, 2.1], x: [0, dx * 0.55, dx], y: [0, dy * 0.55, dy] }}
          transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.5, ease: "easeOut" }}
        />
      ))}
    </div>
  );
};

// Chunky wheels on the ground-side of each car (map edge for vertical lanes).
const Wheels = ({ lane, gold }) => {
  const w = `absolute w-2.5 h-2.5 rounded-full bg-slate-950 border-2 shadow ${gold ? "border-amber-400/90" : "border-slate-400/80"}`;
  if (lane === "bottom") {
    return (<><span className={`${w} -bottom-1.5 left-1`} /><span className={`${w} -bottom-1.5 right-1`} /></>);
  }
  const side = lane === "left" ? "-left-1.5" : "-right-1.5";
  return (<><span className={`${w} ${side} top-1`} /><span className={`${w} ${side} bottom-1`} /></>);
};

const pickRiders = (businesses, n = 10) => {
  const vip = businesses.filter((b) => b.ad_headline || b.has_deals || b.logo_url || b.featured);
  const rest = businesses.filter((b) => !vip.includes(b));
  const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);
  return [...shuffle(vip), ...shuffle(rest)].slice(0, n);
};

const CarFace = ({ b }) => {
  const src = logoFor(b, null, BACKEND_URL);
  if (src) {
    return (
      <span className="relative w-10 h-10 flex items-center justify-center">
        <span className="absolute font-heading font-black text-sm text-sky-300">{(b.name || "?").charAt(0).toUpperCase()}</span>
        <img src={src} alt="" loading="lazy"
          className="relative w-10 h-10 object-contain rounded-md bg-white p-0.5 shadow"
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
      </span>
    );
  }
  if (b.ad_image_url) {
    return <img src={`${BACKEND_URL}${b.ad_image_url}`} alt="" className="w-full h-full object-cover rounded-lg" onError={(e) => { e.currentTarget.style.display = "none"; }} />;
  }
  return <span className="font-heading font-black text-sm text-sky-300">{(b.name || "?").charAt(0).toUpperCase()}</span>;
};

// One train instance. Siblings share lanesInUse so two trains never pile onto
// the same edge. Only the flagship carries the GOLDEN CABOOSE.
const TrainRun = ({ bizRef, fcRef, cabooseRef, lanesInUse, label, initialDelay, onCarTap, theme = "gold", boardRef, withCargo, onCargoTap }) => {
  const [run, setRun] = useState(null); // { lane, dir, riders, caboose, key, duration }
  const skin = THEMES[theme];
  const timerRef = useRef(null);
  const laneRef = useRef(null);
  const cancelledRef = useRef(false);
  const fallbackRef = useRef(null);
  const runKeyRef = useRef(null);

  const buildTrain = () => {
    const pool = bizRef.current || [];
    const firstClass = fcRef.current.slice(0, 6);
    const fcIds = new Set(firstClass.map((b) => b.id));
    const maxCars = pool.length >= BUSY_THRESHOLD ? 16 : 11;
    const standby = pickRiders(pool.filter((b) => !fcIds.has(b.id)), Math.max(4, maxCars - firstClass.length) + 4);
    // No twin cars: one ride per brand name (two PNC branches = one PNC car).
    const seen = new Set();
    return [...firstClass, ...standby].filter((b) => {
      const k = (b.name || b.id || "").toLowerCase().trim();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    }).slice(0, maxCars);
  };

  const releaseLane = () => {
    if (laneRef.current) { lanesInUse.current.delete(laneRef.current); laneRef.current = null; }
  };

  const startRun = () => {
    const riders = buildTrain();
    if (riders.length < 3) return false;
    const free = LANES.filter((l) => !lanesInUse.current.has(l));
    if (!free.length) return false; // every rail busy — wait instead of overlapping
    const lane = free[Math.floor(Math.random() * free.length)];
    lanesInUse.current.add(lane);
    laneRef.current = lane;
    const dir = Math.random() < 0.5 ? 1 : -1;
    if (boardRef) boardRef.current[label] = { nextAt: null, running: true };
    const key = Date.now();
    const duration = 12 + riders.length * 0.3 + Math.random() * 4;
    runKeyRef.current = key;
    setRun({ lane, dir, riders, caboose: cabooseRef ? cabooseRef.current : null, cargo: !!withCargo,
             key, duration });
    // Watchdog: if framer's onAnimationComplete is ever missed (mobile
    // backgrounding, dropped frames), force-finish so this train never dies.
    clearTimeout(fallbackRef.current);
    fallbackRef.current = setTimeout(() => {
      if (!cancelledRef.current && runKeyRef.current === key) done();
    }, (duration + 5) * 1000);
    return true;
  };

  // Retry-safe scheduler: if there aren't enough riders yet when the timer
  // fires, try again in 6s instead of silently killing this train forever.
  const schedule = (delay) => {
    clearTimeout(timerRef.current);
    if (boardRef) boardRef.current[label] = { nextAt: Date.now() + delay, running: false };
    timerRef.current = setTimeout(() => {
      if (cancelledRef.current) return;
      if (!startRun()) schedule(6000);
    }, delay);
  };

  useEffect(() => {
    cancelledRef.current = false;
    schedule(initialDelay);
    return () => {
      cancelledRef.current = true; clearTimeout(timerRef.current); clearTimeout(fallbackRef.current);
      releaseLane();
      if (boardRef) delete boardRef.current[label];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const done = () => {
    if (runKeyRef.current === null) return; // already finished (watchdog/callback both fired)
    runKeyRef.current = null;
    clearTimeout(fallbackRef.current);
    releaseLane();
    setRun(null);
    schedule(5000 + Math.random() * 6000);
  };

  useEffect(() => {
    if (run && localStorage.getItem("airpog_whistle") !== "0") playWhistle();
  }, [run?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const geometry = useMemo(() => {
    if (!run) return null;
    const vertical = run.lane !== "bottom";
    const trainLen = run.riders.length * (CAR_SIZE + GAP) + ENGINE_LEN + (run.caboose ? CABOOSE_LEN + GAP : 0) + 40;
    if (vertical) {
      const from = run.dir === 1 ? window.innerHeight + 40 : -trainLen - 40;
      const to = run.dir === 1 ? -trainLen - 40 : window.innerHeight + 40;
      return { vertical, from, to, style: { [run.lane]: 4, top: 0 } };
    }
    const from = run.dir === 1 ? -trainLen - 40 : window.innerWidth + 40;
    const to = run.dir === 1 ? window.innerWidth + 40 : -trainLen - 40;
    // Ride ABOVE the whole bottom stack (POG STRIP cards + trending pills),
    // measured live so the wheels always clear the pills in any layout state.
    const els = ['[data-testid="trending-rail"]', '[data-testid="business-strip"]']
      .map((s) => document.querySelector(s)).filter(Boolean);
    const topMost = els.length
      ? Math.min(...els.map((el) => el.getBoundingClientRect().top))
      : window.innerHeight - 278;
    const clearance = Math.max(90, window.innerHeight - topMost + 14);
    return { vertical, from, to, style: { bottom: clearance, left: 0 } };
  }, [run]);

  if (!run || !geometry) return null;
  const { vertical, from, to, style } = geometry;
  // Engine leads the train: reverse flex order when moving right/down.
  const flexDir = vertical
    ? (run.dir === -1 ? "flex-col-reverse" : "flex-col")
    : (run.dir === 1 ? "flex-row-reverse" : "flex-row");
  // Engine/caboose art faces right, wheels on the bottom. Orient so the front
  // points along travel AND the wheels face the track (map edge) like the cars.
  const engineTransform = !vertical
    ? (run.dir === -1 ? "scaleX(-1)" : "none")
    : run.lane === "right"
      ? (run.dir === 1 ? "rotate(-90deg)" : "rotate(-90deg) scaleX(-1)")
      : (run.dir === 1 ? "rotate(90deg) scaleX(-1)" : "rotate(90deg)");
  return (
    <AnimatePresence>
      <motion.div
        key={run.key}
        data-testid="pog-express-train"
        data-train-line={label}
        className={`absolute z-[1100] pointer-events-none flex ${flexDir} ${vertical ? (run.lane === "right" ? "items-end" : "items-start") : "items-end"}`}
        style={{ ...style, gap: GAP }}
        initial={vertical ? { y: from } : { x: from }}
        animate={vertical ? { y: to } : { x: to }}
        transition={{ duration: run.duration, ease: "linear" }}
        onAnimationComplete={done}
      >
        <div className="pointer-events-none relative flex items-center justify-center shrink-0"
             style={{ width: vertical ? CAR_SIZE : ENGINE_LEN, height: vertical ? ENGINE_LEN : CAR_SIZE }}>
          <SmokePuffs vertical={vertical} dir={run.dir} lane={run.lane} />
          {isNight() && <Headlight vertical={vertical} dir={run.dir} color={skin.beam} />}
          <img
            src={skin.engine}
            alt="POG EXPRESS engine"
            data-testid="pog-express-engine"
            className="drop-shadow-[0_4px_10px_rgba(0,0,0,0.45)]"
            style={{ width: ENGINE_LEN, maxWidth: "none", transform: engineTransform }}
            draggable={false}
          />
        </div>
        {run.riders.map((b, i) => (
          <button
            key={`${b.id}-${i}`}
            data-testid={b.first_class ? "pog-express-gold-car" : "pog-express-car"}
            onClick={() => onCarTap(b, run.key)}
            className={`pointer-events-auto relative flex items-center justify-center rounded-lg backdrop-blur-sm shadow-md hover:scale-110 transition-transform after:absolute after:-inset-2 after:content-[''] ${
              b.first_class
                ? "bg-slate-900/95 border-2 border-amber-400 shadow-amber-500/40 ring-1 ring-amber-300/40"
                : `bg-slate-900/85 border ${skin.carBorder} hover:border-amber-400`
            }`}
            style={{ width: b.first_class ? CAR_SIZE + 12 : CAR_SIZE, height: b.first_class ? CAR_SIZE + 12 : CAR_SIZE }}
            title={b.name}
          >
            <CarFace b={b} />
            <Wheels lane={run.lane} gold={b.first_class} />
            {b.first_class && (
              <span className="absolute -top-0.5 left-0.5 text-[11px] drop-shadow">👑</span>
            )}
            {(b.has_deals || b.ad_headline) && (
              <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-emerald-400 shadow" />
            )}
          </button>
        ))}
        {run.cargo && (
          <button
            data-testid="pog-express-cargo"
            onClick={onCargoTap}
            className="pointer-events-auto relative flex flex-col items-center justify-center rounded-lg bg-amber-950/95 border-2 border-amber-600/90 shadow-md shadow-amber-900/40 hover:scale-110 transition-transform after:absolute after:-inset-2 after:content-['']"
            style={{ width: CAR_SIZE, height: CAR_SIZE }}
            title="POGZ R US — SECRET CARGO · tap to crack a fresh POG!"
          >
            <span className="text-xl leading-none">📦</span>
            <span className="text-[6px] font-black text-amber-300 tracking-widest mt-0.5">POGZ R US</span>
            <Wheels lane={run.lane} />
          </button>
        )}
        {run.caboose && (
          <button
            data-testid="pog-express-caboose"
            onClick={() => onCarTap(run.caboose, run.key)}
            className="pointer-events-auto relative shrink-0 hover:scale-110 transition-transform after:absolute after:-inset-2 after:content-['']"
            style={{ width: vertical ? CAR_SIZE : CABOOSE_LEN, height: vertical ? CABOOSE_LEN : CAR_SIZE }}
            title={`${run.caboose.name} — GOLDEN CABOOSE`}
          >
            <span className="absolute inset-0 flex items-center justify-center">
              <img src="/pog-express-caboose.png" alt="" className="drop-shadow-[0_4px_10px_rgba(0,0,0,0.45)]"
                   style={{ width: CABOOSE_LEN, maxWidth: "none", transform: engineTransform }} draggable={false} />
            </span>
            <span className="absolute inset-0 flex items-center justify-center pt-1">
              {run.caboose.logo_url ? (
                <img src={`${BACKEND_URL}${run.caboose.logo_url}`} alt="" className="w-5 h-5 object-contain rounded-full bg-white/85 p-0.5 shadow" />
              ) : (
                <span className="font-heading font-black text-[11px] text-amber-900 bg-amber-200/90 rounded-full w-5 h-5 flex items-center justify-center shadow">
                  {(run.caboose.name || "?").charAt(0).toUpperCase()}
                </span>
              )}
            </span>
          </button>
        )}
        <span className={`pointer-events-none text-[8px] font-extrabold uppercase tracking-[0.2em] ${skin.labelCls} bg-slate-900/80 rounded px-1.5 py-0.5 whitespace-nowrap ${vertical ? "mt-2" : "ml-1 mb-3"}`}
              style={vertical ? { writingMode: "vertical-rl" } : undefined}>
          {label}
        </span>
      </motion.div>
    </AnimatePresence>
  );
};

// 🕐 KINGDOM DEPARTURE BOARD — tiny station pill while no train is on screen.
const DepartureBoard = ({ boardRef }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);
  const entries = Object.values(boardRef.current || {});
  if (!entries.length || entries.some((e) => e.running)) return null;
  const upcoming = entries.filter((e) => e.nextAt && e.nextAt > Date.now());
  if (!upcoming.length) return null;
  const secs = Math.ceil((Math.min(...upcoming.map((e) => e.nextAt)) - Date.now()) / 1000);
  if (secs > 45 || secs < 1) return null;
  return (
    <div data-testid="departure-board"
         className="absolute left-2 bottom-[16.25rem] z-[590] pointer-events-none glass rounded-full px-2.5 py-1 text-[10px] font-black text-amber-200 flex items-center gap-1">
      🚂 next train in {secs}s
    </div>
  );
};

// 🚂 THE POG EXPRESS — merchant trains deployed out of POG KINGDOM, running
// random routes along the map edges. Golden-ticket merchants ride first class 👑
// on every train. Busy skies (150+ businesses) get 16-car trains AND a second
// train: THE KINGDOM LINE 🏰.
export const PogExpress = ({ businesses, onCarTap, center, radius }) => {
  const bizRef = useRef(businesses);
  bizRef.current = businesses;
  const fcRef = useRef([]);
  const cabooseRef = useRef(null);
  const lanesInUse = useRef(new Set());
  const boardRef = useRef({});

  useEffect(() => {
    if (!center?.lat) return;
    let live = true;
    const fetchFc = () => {
      axios.get(`${API}/express/first-class`, { params: { lat: center.lat, lng: center.lng, radius: radius || 10 } })
        .then((r) => { if (live) fcRef.current = r.data.riders || []; })
        .catch(() => {});
      axios.get(`${API}/express/caboose`)
        .then((r) => { if (live) cabooseRef.current = r.data.rider || null; })
        .catch(() => {});
    };
    fetchFc();
    const iv = setInterval(fetchFc, 120000);
    return () => { live = false; clearInterval(iv); };
  }, [center?.lat, center?.lng, radius]);

  const [toast, setToast] = useState(null);
  const flashToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 5000);
  };

  const tapCar = (b, trainKey) => {
    axios.post(`${API}/express/${encodeURIComponent(b.id)}/tap`,
      { train_key: String(trainKey || "") }, { withCredentials: true })
      .then((r) => {
        const st = r.data.train_streak;
        if (st?.bonus) flashToast(`🚂 ${st.goal}-TRAIN STREAK! +${st.bonus} POG BUCKS! 🎉`);
      })
      .catch(() => {});
    onCarTap(b);
  };

  const cargoBusy = useRef(false);
  const crackCargo = async () => {
    if (cargoBusy.current) return;
    cargoBusy.current = true;
    try {
      const r = await axios.post(`${API}/express/cargo/crack`, {}, { withCredentials: true, timeout: 6000 });
      const p = r.data.pog || {};
      flashToast(`📦 CARGO CRACKED! Freshly minted ${String(p.pog_id || "POG").toUpperCase()} (${p.rarity || "?"})${p.is_new ? " — NEW to your collection!" : "!"} 🎉`);
    } catch (e) {
      if (e.response?.status === 401) flashToast("📦 Sign in as a POG'R to crack the secret cargo!");
      else flashToast(e.response?.data?.detail || "The crate wouldn't budge — try again!");
    }
    cargoBusy.current = false;
  };

  // 👑 KINGDOM LINE is earned, not lost: entering busy skies (150+ merchants)
  // unlocks the royal train for the WHOLE session — driving into quieter
  // stretches never yanks it off the map mid-ride.
  const busy = (businesses?.length || 0) >= BUSY_THRESHOLD;
  const [kingdomUnlocked, setKingdomUnlocked] = useState(false);
  useEffect(() => {
    if (busy && !kingdomUnlocked) {
      setKingdomUnlocked(true);
      flashToast("👑 KINGDOM LINE UNLOCKED — busy skies! The royal train rides with you all session! 🏰");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);
  return (
    <>
      {toast && (
        <div data-testid="express-toast"
             className="absolute left-1/2 -translate-x-1/2 bottom-[19rem] z-[720] pointer-events-none glass rounded-full px-4 py-2 text-xs font-black text-amber-200 whitespace-nowrap shadow-lg">
          {toast}
        </div>
      )}
      <DepartureBoard boardRef={boardRef} />
      <TrainRun bizRef={bizRef} fcRef={fcRef} cabooseRef={cabooseRef} lanesInUse={lanesInUse}
                label="POG EXPRESS" initialDelay={4000} onCarTap={tapCar} boardRef={boardRef}
                withCargo onCargoTap={crackCargo} />
      {kingdomUnlocked && (
        <TrainRun bizRef={bizRef} fcRef={fcRef} cabooseRef={null} lanesInUse={lanesInUse}
                  label="🏰 KINGDOM LINE" initialDelay={13000} onCarTap={tapCar} theme="royal" boardRef={boardRef} />
      )}
    </>
  );
};
